import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BrandMark from '../components/BrandMark';
import LangSwitch from '../components/LangSwitch';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';

function HeaderBar({ rightSlot }) {
  const { t } = useT();
  return (
    <header className="w-full px-4 sm:px-10 lg:px-16 pt-6 sm:pt-10 lg:pt-12 pb-4 grid grid-cols-3 items-center gap-2 font-pressura">
      <div className="justify-self-start min-w-0">
        <Link
          to="/"
          className="text-[0.6rem] sm:text-xs tracking-[0.12em] sm:tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 whitespace-nowrap"
          style={{ fontWeight: 700 }}
          data-testid="buy-back-link"
        >
          {t('nav.back')}
        </Link>
      </div>
      <div className="justify-self-center" aria-hidden="true" />
      <div className="justify-self-end min-w-0 truncate">{rightSlot}</div>
    </header>
  );
}

export default function Buy() {
  const { t } = useT();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isGift, setIsGift] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [giftMessage, setGiftMessage] = useState('');

  const startCheckout = async () => {
    if (isGift && !recipientEmail.trim()) {
      setError(t('buy.giftEmail'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        origin_url: window.location.origin,
        is_gift: isGift,
        gift_recipient_email: isGift ? recipientEmail.trim() : null,
        gift_message: isGift ? giftMessage.trim() : null,
      };
      const r = await api.post('/checkout/session', payload);
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (e) {
      // Show the server's actual message when available so the cause
      // (e.g. "Payments temporarily unavailable" — expired Stripe key) is
      // visible instead of a generic fallback. Log to console for support.
      const serverDetail =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        '';
      console.error('Checkout error:', {
        status: e?.response?.status,
        detail: serverDetail,
        error: e?.message,
      });
      setError(serverDetail || t('buy.error'));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col fade-in font-pressura" data-testid="buy-screen">
      <HeaderBar
        rightSlot={
          <Link
            to="/redeem"
            className="text-[0.6rem] sm:text-xs tracking-[0.12em] sm:tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 whitespace-nowrap"
            style={{ fontWeight: 700 }}
            data-testid="buy-redeem-link"
          >
            {t('buy.iHaveLicense')}
          </Link>
        }
      />

      <main className="flex-1 flex flex-col items-center pt-2 pb-24 px-4">
        <Link to="/" className="block hover:opacity-90 transition-opacity duration-300" aria-label="Connect to Love — home">
          <BrandMark size="hero" />
        </Link>

        <div className="text-center max-w-2xl w-full mt-12 sm:mt-16">
          <p
            className="font-pressura font-bold text-[#000000] leading-snug uppercase tracking-[0.04em]"
            style={{ fontSize: 'clamp(1.0625rem, 2vw, 1.5rem)' }}
            data-testid="buy-headline"
          >
            {t('buy.headline')}
          </p>

          <div className="space-y-4 mt-8 mb-10 max-w-lg mx-auto" data-testid="buy-blurb">
            <p className="font-pressura text-[#000000] opacity-85 leading-relaxed text-sm sm:text-base">
              {t('buy.descLine1')}
            </p>
            <p className="font-pressura text-[#000000] opacity-85 leading-relaxed text-sm sm:text-base">
              {t('buy.descLine2')}
            </p>
            <p className="font-pressura text-[#000000] opacity-85 leading-relaxed text-sm sm:text-base">
              {t('buy.includesBook')}
            </p>
          </div>

          <div className="font-pressura text-3xl md:text-4xl text-[#000000] font-bold mb-1" data-testid="buy-price">
            €8.88
          </div>
          <p className="font-pressura text-xs uppercase tracking-[0.18em] text-[#000000] opacity-50 mb-8">
            {t('buy.priceCaption')}
          </p>

          <div className="flex justify-center mb-10" data-testid="buy-lang-switch-wrap">
            <LangSwitch testId="buy-lang-switch" />
          </div>

          <div className="mb-10">
            <label className="inline-flex items-center gap-3 cursor-pointer select-none" data-testid="buy-gift-toggle-wrap">
              <input
                type="checkbox"
                checked={isGift}
                onChange={(e) => { setIsGift(e.target.checked); setError(''); }}
                className="accent-black w-4 h-4"
                data-testid="buy-gift-toggle"
              />
              <span className="font-pressura text-sm tracking-[0.12em] uppercase text-[#000000] font-bold">
                {t('buy.giftToggle')}
              </span>
            </label>

            {isGift && (
              <div className="mt-8 max-w-md mx-auto text-left space-y-5 fade-in" data-testid="buy-gift-fields">
                <div>
                  <label className="font-pressura text-xs uppercase tracking-[0.18em] text-[#000000] opacity-70 font-bold block mb-2" htmlFor="buy-gift-email">
                    {t('buy.giftEmail')}
                  </label>
                  <input
                    id="buy-gift-email"
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    className="ctl-input font-pressura"
                    placeholder={t('buy.giftEmailPh')}
                    autoComplete="off"
                    spellCheck="false"
                    data-testid="buy-gift-email"
                  />
                </div>
                <div>
                  <label className="font-pressura text-xs uppercase tracking-[0.18em] text-[#000000] opacity-70 font-bold block mb-2" htmlFor="buy-gift-message">
                    {t('buy.giftMessage')}
                  </label>
                  <textarea
                    id="buy-gift-message"
                    value={giftMessage}
                    onChange={(e) => setGiftMessage(e.target.value)}
                    className="ctl-textarea font-pressura min-h-[80px]"
                    placeholder={t('buy.giftMessagePh')}
                    data-testid="buy-gift-message"
                  />
                </div>
                <p className="font-pressura text-xs opacity-70">{t('buy.giftHint')}</p>
              </div>
            )}
          </div>

          <button
            className="ctl-shimmer-copper bg-transparent border-0 p-0 cursor-pointer font-pressura font-bold tracking-[0.18em] uppercase hover:opacity-80 transition-opacity duration-300 disabled:opacity-40"
            style={{ fontSize: 'clamp(1rem, 1.6vw, 1.25rem)', textDecoration: 'underline', textUnderlineOffset: '0.4em', textDecorationThickness: '2px' }}
            onClick={startCheckout}
            disabled={loading || (isGift && !recipientEmail.trim())}
            data-testid="buy-checkout-btn"
          >
            {loading ? t('buy.ctaLoading') : t('buy.cta')}
          </button>

          {error && (
            <p className="mt-8 text-sm text-[#8B2A18] font-pressura" data-testid="buy-error">
              {error}
            </p>
          )}

          <p className="font-pressura mt-14 text-xs max-w-md mx-auto opacity-60 leading-relaxed">
            {t('buy.disclaimer')}
          </p>
        </div>
      </main>
    </div>
  );
}
